import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenVaccinationsIndicatorC2Data } from "@/services/esus/indicator/esf/c2/getDetailedCitizensVaccinationsIndicatorC2";

import { IndicatorEsfC2CitizensVaccinationsFilters } from "./IndicatorEsfC2CitizensVaccinationsFilters";
import {
	getCitizenVaccinationStatusDescription,
	IndicatorEsfC2CitizensVaccinationsTable,
} from "./IndicatorEsfC2CitizensVaccinationsTable";
import { IndicatorEsfC2CitizensVaccinationsHistory } from "./IndicatorEsfC2CitizensVaccinationsHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC2CitizensVaccinationsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensVaccinations: Paginated<DetailedCitizenVaccinationsIndicatorC2Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensVaccinationsIndicatorC2: () => Promise<
		DetailedCitizenVaccinationsIndicatorC2Data[]
	>;
};

export function IndicatorEsfC2CitizensVaccinations({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensVaccinations,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensVaccinationsIndicatorC2,
}: Readonly<IndicatorEsfC2CitizensVaccinationsProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithVaccinations, setCitizenWithVaccinations] =
		useState<DetailedCitizenVaccinationsIndicatorC2Data>(
			new DetailedCitizenVaccinationsIndicatorC2Data()
		);
	const [isLoadingCitizenWithVaccinations, setIsLoadingCitizenWithVaccinations] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(
		citizenVaccinations: DetailedCitizenVaccinationsIndicatorC2Data
	) {
		setIsLoadingCitizenWithVaccinations(true);
		setCitizenWithVaccinations(citizenVaccinations);
		setIsLoadingCitizenWithVaccinations(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedCitizensVaccinationsIndicatorC2();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformCitizensWithVaccinationsToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Vacinações - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformCitizensWithVaccinationsToGenericObjectArray(
		citizensWithVaccinations: DetailedCitizenVaccinationsIndicatorC2Data[]
	) {
		return citizensWithVaccinations.map(
			(citizenWithVaccinations) =>
				({
					Nome: citizenWithVaccinations.fullName,
					DN: dayjs(citizenWithVaccinations.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenWithVaccinations.birthdate),
					Documento: citizenWithVaccinations.cpf ?? citizenWithVaccinations.cns ?? "",
					"Nome da mãe": citizenWithVaccinations.motherName ?? "",
					Area: citizenWithVaccinations.area ?? "FA",
					"Ine Equipe": citizenWithVaccinations.healthTeam?.ine ?? "",
					"Nome Equipe": citizenWithVaccinations.healthTeam?.name ?? "",
					Penta: citizenWithVaccinations.vaccinationResume.penta,
					VIP: citizenWithVaccinations.vaccinationResume.vip,
					Pneumo: citizenWithVaccinations.vaccinationResume.pneumococica,
					Tríplice: citizenWithVaccinations.vaccinationResume.tripleViral,
					"Ref. Pneumo": citizenWithVaccinations.vaccinationResume.refPneumococica,
					"Ref. Dtp": citizenWithVaccinations.vaccinationResume.refPenta,
					"Ref. Vip": citizenWithVaccinations.vaccinationResume.refVip,
					"Status Geral": getCitizenVaccinationStatusDescription(
						citizenWithVaccinations.vaccinationStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithVaccinations || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC2CitizensVaccinationsFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC2CitizensVaccinationsTable
							citizensVaccinations={paginatedCitizensVaccinations.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensVaccinations?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensVaccinations.pageSize}
								totalPages={paginatedCitizensVaccinations.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Vacinas aplicadas em crianças até os 2 anos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC2CitizensVaccinationsHistory
							citizenWithVaccinations={citizenWithVaccinations}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
